import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LineReveal } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGNpdHlzY2FwZSUyMGFyY2hpdGVjdHVyZSUyMG1vZGVybnxlbnwwfHx8fDE3ODU4NDEyMjR8MA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollDown = () =>
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={ref}
      className="relative h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Dubai skyline" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-quantum-bg via-transparent to-quantum-bg/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 flex h-full flex-col justify-end px-6 md:px-12 lg:px-24 pb-20 md:pb-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-quantum-gold mb-6"
        >
          Dubai · United Arab Emirates
        </motion.p>

        <h1 className="font-serif-q font-light text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.9] tracking-tighter text-quantum-text max-w-6xl">
          <LineReveal delay={0.5}>Elevating global</LineReveal>
          <LineReveal delay={0.66}>
            standards of <span className="italic text-quantum-gold">service</span>
          </LineReveal>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-lg text-quantum-muted leading-relaxed">
            A globally-minded house of services — marketing, advertising, hospitality and IT consulting — crafted
            for ambitious brands and individuals worldwide.
          </p>
          <button
            onClick={scrollDown}
            data-testid="hero-scroll-cue"
            className="group flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-quantum-text hover:text-quantum-gold transition-colors"
          >
            <span>Discover</span>
            <span className="grid place-items-center h-11 w-11 rounded-full border border-white/20 group-hover:border-quantum-gold transition-colors">
              <ArrowDown size={16} className="animate-bounce" />
            </span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
